"use client";

import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

const ContactCTA = () => {
  return (
    <section className="py-20 px-6 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5">
      <div className="container max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Transform Your <span className="text-gradient">Business Operations</span>?
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            You&apos;ve seen the journey, the expertise, and the results. Now it&apos;s time to write your own transformation story.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12"
        >
          <div className="bg-card border border-border rounded-2xl p-8 text-left">
            <MessageCircle className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-2xl font-bold mb-4">Start with a Conversation</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Let&apos;s discuss your specific challenges and explore how AI automation can transform your operations. 
              No sales pitch—just an honest conversation about possibilities.
            </p>
            <Button 
              size="lg" 
              className="w-full group"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Get in Touch
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 text-left">
            <Calendar className="w-12 h-12 text-primary mb-4" />
            <h3 className="text-2xl font-bold mb-4">Book a Strategy Session</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Schedule a 15-minute automation triage call where we&apos;ll identify your biggest time-wasters 
              and map out a transformation roadmap.
            </p>
            <Button 
              variant="outline" 
              size="lg" 
              className="w-full group"
              onClick={() => window.open('https://calendly.com/abduldev', '_blank')}
            >
              Schedule Call
              <Calendar className="ml-2 w-4 h-4 group-hover:scale-110 transition-transform" />
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true, margin: "-100px" }}
          className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-primary mb-2">6 Weeks</div>
              <div className="text-sm text-muted-foreground">To full automation</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary mb-2">80%</div>
              <div className="text-sm text-muted-foreground">Time savings guaranteed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary mb-2">3 Months</div>
              <div className="text-sm text-muted-foreground">Typical ROI payback</div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-border/50">
            <p className="text-lg font-semibold text-primary">
              Join 100+ businesses that have already transformed their operations
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
          className="mt-8"
        >
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Available for new projects
            </span>
            <span>•</span>
            <span>Typically respond within 2 hours</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactCTA; 